import * as angular from 'angular';
import { IAugmentedJQuery, IDocumentService, module } from 'angular';
import { ISchemas } from '../../../core/interfaces/schemas.interface';
import { IDataTypeMap } from '../../../core/services/data-types/data-type.service';
import { IDialog } from '../../../core/services/dialog.service';
import { IInjectStyleService } from '../../../core/services/inject-style.service';
import IDialogOptions = angular.material.IDialogOptions;
import { IMonoOccurrence } from '../../../core/services/mono-occurrence.service';
import { ISourceDetailsManager } from '../../../core/services/source-details-manager.service';
import { IMultiOccurrence } from '../../../core/services/multi-occurrence.service';

export interface IDonneesEnregistrementService {
    active: boolean;
    activate(): void;
    deactivate(): void;
    toggleActivation(): void;
    getDataWithLovKey(data: any, dataType: any): any;
}

export interface IDonneesEnregistrementDetails {
    data: any;
    schema: ISchemas;
    dataTypes: IDataTypeMap;
    table?: string;
}

export default module('app.services.donnees-enregistrement', []).factory('donneesEnregistrement', DonneesEnregistrementFactory);

/* @ngInject */
function DonneesEnregistrementFactory(
    DialogDonneesEnregistrement: IDialog,
    injectStyle: IInjectStyleService,
    $document: IDocumentService,
    sourceDetailsManager: ISourceDetailsManager
): IDonneesEnregistrementService {
    injectStyle(`.ex-donnees-enregistrement-active * {
            cursor: help !important;
        }
        
        .ex-donnees-enregistrement-active ex-card-cycle>ex-card>md-card md-card-content:hover,
        .ex-donnees-enregistrement-active ex-card-content>ex-formulaire>form:hover,
        .ex-donnees-enregistrement-active ex-saisie-content>ex-formulaire>form:hover,
        .ex-donnees-enregistrement-active .ex-multi-occurrence-accordeon-bloc:hover,
        .ex-donnees-enregistrement-active ex-data-list:hover,
        .ex-donnees-enregistrement-active [multi-occurrence] .ex-grid-row:hover .ex-grid-col {
            background: rgb(255, 255, 200) !important;
        }`
    );

    class DonneesEnregistrement implements IDonneesEnregistrementService {
        active: boolean = false;

        activate() {
            this.active = true;

            angular.element(document.body)
                .addClass('ex-donnees-enregistrement-active');

            $document.on('click.donneesEnregistrement', 'ex-saisie-content>ex-formulaire, ex-card-content>ex-formulaire', (event: any) => {
                event.preventDefault();
                event.stopPropagation();

                const el: IAugmentedJQuery = angular.element(<HTMLElement>event.currentTarget);
                const monoOccurrence = el.controller('exMonoOccurrence').monoOccurrence as IMonoOccurrence;
                const params: any = monoOccurrence?.stateParams;

                const dataWithKey = this.getDataWithLovKey(monoOccurrence.data, monoOccurrence.dataTypes);

                sourceDetailsManager.fetch(monoOccurrence.getNomSourceDetails(), null, params).then((sourceDetails) => {
                    this.open(event, dataWithKey, monoOccurrence.dataTypes, monoOccurrence.schema, sourceDetails.table);
                });
            });

            $document.on('click.donneesEnregistrement', '[multi-occurrence] .ex-grid-row, [multi-occurrence] .ex-multi-occurrence-accordeon-bloc', (event: any) => {
                event.preventDefault();
                event.stopPropagation();

                const el: IAugmentedJQuery = angular.element(<HTMLElement>event.currentTarget);
                const multiOccurrence = el.closest('ex-multi-occurrence').controller('exMultiOccurrence').multiOccurrence as IMultiOccurrence;

                const index = el.index();
                const params: any = multiOccurrence?.stateParams

                const dataWithKey = this.getDataWithLovKey(multiOccurrence.dataList[index], multiOccurrence.dataTypes);

                sourceDetailsManager.fetch(multiOccurrence.getNomSourceDetails(), null, params).then((sourceDetails) => {
                    this.open(event, dataWithKey, multiOccurrence.dataTypes, multiOccurrence.schema, sourceDetails.table);
                });
            });

            $document.on('click.donneesEnregistrement', 'ex-card-cycle>ex-card>md-card md-card-content', (event: any) => {
                event.preventDefault();
                event.stopPropagation();

                const el: IAugmentedJQuery = angular.element(<HTMLElement>event.currentTarget);
                const ctrl = el.closest('ex-card-cycle').controller('exCardCycle');

                const data = { ...ctrl.monoOccurrence.etatActuel };
                delete data.$promise;
                delete data.$resolved;

                const dataWithKey = this.getDataWithLovKey(data, ctrl.dataTypes);

                this.open(event, dataWithKey, ctrl.dataTypes, data);
            });

            $document.on('click.donneesEnregistrement', 'ex-data-list', (event: any) => {
                event.preventDefault();
                event.stopPropagation();

                const el: IAugmentedJQuery = angular.element(<HTMLElement>event.currentTarget);
                const ctrl = el.controller('exDataList') || el.controller('exDataListAccordeon');
                const multiOccurrence = el.closest('ex-multi-occurrence').controller('exMultiOccurrence')?.multiOccurrence as IMultiOccurrence;
                const params: any = multiOccurrence?.stateParams;

                const dataWithKey = this.getDataWithLovKey(ctrl.data, ctrl.dataTypes);

                if (multiOccurrence) {
                    sourceDetailsManager.fetch(multiOccurrence.getNomSourceDetails(), null, params).then((sourceDetails) => {
                        this.open(event, dataWithKey, ctrl.dataTypes, ctrl.schemas, sourceDetails.table);
                    });
                } else {
                    this.open(event, dataWithKey, ctrl.dataTypes, ctrl.schemas);
                }
            });
        }

        // Ajoute la clé au champ de description de toutes les lovs dans data
        getDataWithLovKey(data: any, dataType: any): any {
            let keyFirstDef: string;
            const dataCopy = { ...data };

            // Pour tous les LovDataType dans dataType
            for (const key in dataType) {
                if (
                    dataType[key]?.params?.type !== 'lov'
                    || !dataType[key].params?.description[0]
                    || !dataCopy[key]
                ) {
                    continue;
                }

                keyFirstDef = dataType[key].params?.description[0];
                const description = dataCopy[key + '__' + keyFirstDef] || dataCopy[keyFirstDef]
                // ajoute la clé de la lov à la valeur du premier desc du data 
                dataCopy[key + '__' + keyFirstDef] = ` { clé : ${dataCopy[key]} } ${description}`;
            }

            return dataCopy;
        }

        deactivate() {
            $document.off('.donneesEnregistrement');
            angular.element(document.body).removeClass('ex-donnees-enregistrement-active');
            this.active = false;
        }

        toggleActivation() {
            if (this.active) {
                this.deactivate();
            } else {
                this.activate();
            }
        }

        private open(event: MouseEvent, data: any, dataTypes: IDataTypeMap, schema: ISchemas, table?: string) {
            const dialogOptions: IDialogOptions = {
                targetEvent: event,
                locals: {
                    data: data,
                    dataTypes: dataTypes,
                    schema: schema,
                } as IDonneesEnregistrementDetails,
            };

            if (table) {
                dialogOptions.locals.table = table;
            }

            DialogDonneesEnregistrement.show(dialogOptions);

            this.deactivate();
        }
    }

    return new DonneesEnregistrement();
}
